/** SUNTRONIX ONE — Administration → Omnichannel Service Intake. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Mail, Globe, Phone, Code, MessageSquare, Users, Building2,
  Trash2, Sparkles, Send, Route } from "lucide-react";

const din = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const CH_ICON = { CUSTOMER_HUB: Building2, EMPLOYEE_HUB: Users, EMAIL: Mail,
  WEB_FORM: Globe, PHONE: Phone, API: Code, CHAT: MessageSquare, WHATSAPP: MessageSquare };

export default function OmnichannelIntake() {
  const [meta, setMeta] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [rules, setRules] = useState([]);
  const [rule, setRule] = useState({ name: "", order: 100, queue: "SERVICE_DESK", assigned_team: "", skills: "", match_type: "", match_priority: "" });
  const [test, setTest] = useState({ subject: "", description: "", email: "" });
  const [classify, setClassify] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/intake/meta").then(({ data }) => { setMeta(data); setCfg(data.config); }).catch(() => {});
    api.get("/intake/routing-rules").then(({ data }) => setRules(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    try { await api.put("/intake/config", cfg); toast.success("Config saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addRule = async () => {
    if (!rule.name || !rule.queue) return toast.error("Name & queue required");
    const match = {};
    if (rule.match_type) match.ticket_type = rule.match_type;
    if (rule.match_priority) match.priority = rule.match_priority;
    try {
      await api.post("/intake/routing-rules", { name: rule.name, order: Number(rule.order) || 100,
        match, queue: rule.queue, assigned_team: rule.assigned_team || null,
        skills: rule.skills.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Rule added"); setRule({ ...rule, name: "", skills: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delRule = async (id) => { await api.delete(`/intake/routing-rules/${id}`); load(); };

  const runClassify = async () => {
    try { const { data } = await api.post("/intake/classify", { subject: test.subject, description: test.description, requester_email: test.email });
      setClassify(data);
    } catch { toast.error("Classify failed"); }
  };
  const runTest = async () => {
    if (!test.subject || !test.description) return toast.error("Subject & description required");
    try { const { data } = await api.post("/intake/web-form", { name: test.email?.split("@")[0] || "Tester", email: test.email || "test@example.com", subject: test.subject, description: test.description });
      setResult(data); toast.success(`${data.action} ${data.ticket_number}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!meta || !cfg) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-[1100px] space-y-4" data-testid="omnichannel-page">
      <header className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><Inbox className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">Omnichannel Service Intake</div>
          <div className="text-[11px] text-slate-500">Every channel feeds ONE Service Desk — no separate email/portal/phone tickets</div>
        </div>
      </header>

      {/* Channels */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8" data-testid="intake-channels">
        {meta.channels.map((c) => {
          const Icon = CH_ICON[c.key] || Inbox;
          return (
            <div key={c.key} className={`rounded-xl border p-2.5 text-center ${c.enabled ? "border-slate-200 bg-white" : "border-dashed border-slate-300 bg-slate-50"}`} data-testid={`intake-ch-${c.key}`}>
              <Icon className={`mx-auto h-4 w-4 ${c.enabled ? "text-teal-700" : "text-slate-400"}`} />
              <div className="mt-1 text-[9.5px] font-semibold text-slate-700">{c.key.replace(/_/g, " ")}</div>
              <div className={`text-[8px] font-semibold ${c.enabled ? "text-emerald-600" : "text-amber-600"}`}>{c.enabled ? "ACTIVE" : "DISABLED"}</div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {/* Config */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="intake-config">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold"><Mail className="h-3.5 w-3.5" /> Email-to-ticket &amp; duplicate control</div>
          <label className="block text-[10px] text-slate-500">Support mailbox
            <input value={cfg.support_mailbox || ""} onChange={(e) => setCfg({ ...cfg, support_mailbox: e.target.value })} className={din} data-testid="intake-mailbox" /></label>
          <div className="mt-1 rounded bg-slate-50 px-2 py-1 text-[9.5px] font-mono text-slate-500">Webhook: {process.env.REACT_APP_BACKEND_URL}/api/intake/email</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="text-[10px] text-slate-500">Duplicate window (days)
              <input type="number" value={cfg.duplicate_window_days} onChange={(e) => setCfg({ ...cfg, duplicate_window_days: Number(e.target.value) })} className={din} /></label>
            <label className="text-[10px] text-slate-500">Default queue
              <select value={cfg.default_queue} onChange={(e) => setCfg({ ...cfg, default_queue: e.target.value })} className={din}>
                {meta.queues.map((q) => <option key={q}>{q}</option>)}</select></label>
          </div>
          <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-600">
            <input type="checkbox" checked={cfg.match_by_subject} onChange={(e) => setCfg({ ...cfg, match_by_subject: e.target.checked })} data-testid="intake-match-subject" /> Match by customer + subject</label>
          <label className="mt-1 flex items-center gap-2 text-[11px] text-slate-600">
            <input type="checkbox" checked={cfg.reopen_on_reply} onChange={(e) => setCfg({ ...cfg, reopen_on_reply: e.target.checked })} /> Reopen resolved ticket on new reply</label>
          <button onClick={saveCfg} data-testid="intake-save-config" className="mt-3 rounded-md bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white">Save config</button>
        </div>

        {/* Test / classify */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="intake-test">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold"><Sparkles className="h-3.5 w-3.5 text-violet-600" /> Test intake &amp; AI classification</div>
          <input value={test.subject} onChange={(e) => setTest({ ...test, subject: e.target.value })} placeholder="Subject" className={din} data-testid="intake-test-subject" />
          <textarea value={test.description} onChange={(e) => setTest({ ...test, description: e.target.value })} rows={2} placeholder="Description" className={din} data-testid="intake-test-desc" />
          <input value={test.email} onChange={(e) => setTest({ ...test, email: e.target.value })} placeholder="Requester email" className={din + " font-mono"} />
          <div className="mt-2 flex gap-2">
            <button onClick={runClassify} data-testid="intake-classify" className="rounded-md border border-violet-300 px-3 py-1.5 text-[11px] font-semibold text-violet-700">Classify</button>
            <button onClick={runTest} data-testid="intake-run-test" className="inline-flex items-center gap-1 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white"><Send className="h-3.5 w-3.5" /> Create/Update ticket</button>
          </div>
          {classify && (
            <div className="mt-2 rounded-md border border-violet-200 bg-violet-50 p-2 text-[11px]" data-testid="intake-classify-result">
              <div><b>Type:</b> {classify.suggestion.ticket_type} · <b>Priority:</b> {classify.suggestion.priority} · <b>Queue:</b> {classify.suggestion.queue}</div>
              {classify.customer_candidate && <div className="text-emerald-700">Customer candidate: {classify.customer_candidate.customer_name || classify.customer_candidate.match_type}</div>}
              <div className="text-[9.5px] text-slate-500">{classify.note}</div>
            </div>
          )}
          {result && <div className="mt-2 rounded-md bg-teal-50 px-2 py-1 text-[11px] text-teal-800" data-testid="intake-test-result">{result.action.toUpperCase()} · {result.ticket_number} · queue {result.ticket.queue}</div>}
        </div>
      </div>

      {/* Routing rules */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="intake-rules">
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold"><Route className="h-3.5 w-3.5" /> Auto-routing rules ({rules.length})</div>
        {rules.map((r) => (
          <div key={r.id} className="flex items-center gap-2 border-t border-slate-100 py-1.5 text-[11px]" data-testid={`intake-rule-${r.id}`}>
            <span className="text-slate-400">#{r.order}</span>
            <span className="font-semibold">{r.name}</span>
            <span className="text-slate-500">{Object.entries(r.match || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "any"}</span>
            <span className="ml-auto rounded bg-teal-100 px-1.5 py-0.5 text-[9px] font-semibold text-teal-800">→ {r.queue}</span>
            {r.assigned_team && <span className="text-slate-500">{r.assigned_team}</span>}
            <button onClick={() => delRule(r.id)} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-6">
          <input value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} placeholder="Rule name" className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" data-testid="rule-name" />
          <select value={rule.match_type} onChange={(e) => setRule({ ...rule, match_type: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]">
            <option value="">Any type</option>{meta.ticket_types.map((t) => <option key={t}>{t}</option>)}</select>
          <select value={rule.match_priority} onChange={(e) => setRule({ ...rule, match_priority: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]">
            <option value="">Any pri</option>{["P1", "P2", "P3", "P4"].map((p) => <option key={p}>{p}</option>)}</select>
          <select value={rule.queue} onChange={(e) => setRule({ ...rule, queue: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" data-testid="rule-queue">
            {meta.queues.map((q) => <option key={q}>{q}</option>)}</select>
          <input value={rule.skills} onChange={(e) => setRule({ ...rule, skills: e.target.value })} placeholder="skills (csv)" className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" />
          <button onClick={addRule} data-testid="rule-add" className="rounded-md bg-teal-800 px-2 py-1.5 text-[11px] font-semibold text-white">+ Rule</button>
        </div>
      </div>
    </div>
  );
}
